/**
 * Spec 2026-09-24 §6.2/§6.3/§8.1 — aprovar um registro passando pela sobrescrita de perna.
 *
 * `approve()` devolve uma Promise que só resolve quando o fluxo TERMINA: aprovado direto
 * (prévia vazia), aprovado depois do modal, ou cancelado. Assim as telas trocam uma linha
 * (`approveContainer` → `approve`) e mantêm o `catch` + `notifyError` de hoje.
 *
 * D-W5: com pagamento prioritário, cada toggle rechama a prévia (debounce) com as decisões
 * para trazer o `effectiveValue.after` exato; o resultado vai para `effectiveValues`, nunca
 * para `conflicts` (trocar `conflicts` resetaria as decisões do modal — D-W2).
 */
import { getCurrentScope, onScopeDispose, ref } from 'vue'
import Network from '@/Network'
import type {
  DSAttendanceApprovePayload,
  DSAttendanceLegOverwriteDecision,
  DSAttendanceLegOverwritePayload
} from '@/Network/Types/Requests/DsAttendanceRequest'
import type {
  EffectiveValue,
  LegOverwriteConflict
} from '@/Network/Types/Responses/DsAttendanceLegOverwriteResponses'
import { ServiceError } from '@/utils/apiError'
import {
  LEG_OVERWRITE_FROZEN_MESSAGE,
  extractOverwriteConflicts,
  isDecisionWithoutConflictError,
  isLegOverwriteFrozenError,
  legOverwriteInlineError
} from '../utils/leg-overwrite'

export type LegOverwriteApprovalOutcome = 'APPROVED' | 'CANCELLED'

export const EFFECTIVE_VALUE_DEBOUNCE_MS = 300

/** Estado do recálculo do valor por `doctorStationId`: ausente = valor em `effectiveValues` vale. */
export type EffectiveValueStatus = 'PENDING' | 'ERROR'

interface PendingApproval {
  containerId: number | string
  basePayload: DSAttendanceApprovePayload
  resolve: (outcome: LegOverwriteApprovalOutcome) => void
  reject: (error: unknown) => void
}

export function useLegOverwriteApproval() {
  const isOpen = ref(false)
  const conflicts = ref<LegOverwriteConflict[]>([])
  const effectiveValues = ref<Record<number, EffectiveValue | null>>({})
  const effectiveValueStatus = ref<Record<number, EffectiveValueStatus>>({})
  const isSubmitting = ref(false)
  const errorMessage = ref<string | null>(null)
  const staleNotice = ref(false)
  let pending: PendingApproval | null = null
  /** `approve()` em curso (prévia inicial + POST direto), antes de haver modal aberto. */
  let inFlight = false
  let refreshTimer: ReturnType<typeof setTimeout> | null = null
  let refreshSequence = 0

  /** 409 congelado ganha o texto que orienta a reabrir o pós-plantão. */
  function toFriendlyError(error: unknown): unknown {
    return isLegOverwriteFrozenError(error)
      ? new ServiceError(LEG_OVERWRITE_FROZEN_MESSAGE, error)
      : error
  }

  function clearRefresh(): void {
    if (refreshTimer) clearTimeout(refreshTimer)
    refreshTimer = null
    refreshSequence += 1 // invalida qualquer resposta em voo
  }

  function openModal(
    containerId: number | string,
    basePayload: DSAttendanceApprovePayload,
    found: LegOverwriteConflict[],
    stale: boolean
  ): Promise<LegOverwriteApprovalOutcome> {
    conflicts.value = found
    effectiveValues.value = {}
    effectiveValueStatus.value = {}
    staleNotice.value = stale
    errorMessage.value = null
    isOpen.value = true
    return new Promise((resolve, reject) => {
      pending = { containerId, basePayload, resolve, reject }
    })
  }

  function settle(): PendingApproval | null {
    const current = pending
    pending = null
    clearRefresh()
    isOpen.value = false
    conflicts.value = []
    effectiveValues.value = {}
    effectiveValueStatus.value = {}
    staleNotice.value = false
    errorMessage.value = null
    return current
  }

  /**
   * 409 `OVERWRITE_CONFIRMATION_REQUIRED` → conflitos atuais. `null` = não é este erro.
   * Corrida na criação da fixação chega com `conflicts: []` (BE plan, Spec deviation 5):
   * sinal para rechamar a prévia.
   */
  async function racedConflicts(
    containerId: number | string,
    error: unknown
  ): Promise<LegOverwriteConflict[] | null> {
    const raced = extractOverwriteConflicts(error)
    if (raced === null || raced.length > 0) return raced
    const preview = await Network.dsAttendance.getLegOverwritePreview(containerId)
    return preview?.conflicts ?? []
  }

  /** Conflitos atualizados pela corrida (409 com payload ou 422 decisão sem conflito). */
  function showRacedConflicts(found: LegOverwriteConflict[]): void {
    clearRefresh()
    conflicts.value = found
    effectiveValues.value = {}
    effectiveValueStatus.value = {}
    staleNotice.value = true
  }

  async function approve(
    containerId: number | string,
    basePayload: DSAttendanceApprovePayload
  ): Promise<LegOverwriteApprovalOutcome> {
    // Clique duplo / Ctrl+Enter durante a prévia, o POST direto ou com o modal aberto:
    // o fluxo em curso é o único.
    if (inFlight || pending || isSubmitting.value) return 'CANCELLED'
    inFlight = true
    try {
      return await startApproval(containerId, basePayload)
    } finally {
      inFlight = false
    }
  }

  async function startApproval(
    containerId: number | string,
    basePayload: DSAttendanceApprovePayload
  ): Promise<LegOverwriteApprovalOutcome> {
    const preview = await Network.dsAttendance.getLegOverwritePreview(containerId)
    const found = preview?.conflicts ?? []
    if (found.length > 0) return openModal(containerId, basePayload, found, false)

    try {
      await Network.dsAttendance.approveContainer(containerId, basePayload)
      return 'APPROVED'
    } catch (error) {
      const raced = await racedConflicts(containerId, error)
      if (raced === null) throw toFriendlyError(error)
      if (raced.length > 0) return openModal(containerId, basePayload, raced, true)
      // 409 com `conflicts: []` e prévia rebuscada vazia: a corrida já se resolveu.
      return retryDirectApproval(containerId, basePayload)
    }
  }

  /** Uma única nova tentativa do POST direto; esbarrar em conflito real abre o modal. */
  async function retryDirectApproval(
    containerId: number | string,
    basePayload: DSAttendanceApprovePayload
  ): Promise<LegOverwriteApprovalOutcome> {
    try {
      await Network.dsAttendance.approveContainer(containerId, basePayload)
      return 'APPROVED'
    } catch (error) {
      const raced = extractOverwriteConflicts(error)
      if (raced && raced.length > 0) return openModal(containerId, basePayload, raced, true)
      throw toFriendlyError(error)
    }
  }

  /** 422 decisão sem conflito = prévia velha: rebusca; sem conflito sobrando, aprova direto. */
  async function retryAfterVanishedConflict(current: PendingApproval): Promise<void> {
    const preview = await Network.dsAttendance.getLegOverwritePreview(current.containerId)
    const remaining = preview?.conflicts ?? []
    if (remaining.length > 0) {
      showRacedConflicts(remaining)
      return
    }
    await Network.dsAttendance.approveContainer(current.containerId, current.basePayload)
    settle()?.resolve('APPROVED')
  }

  async function handleConfirmFailure(current: PendingApproval, error: unknown): Promise<void> {
    let raced: LegOverwriteConflict[] | null
    try {
      raced = await racedConflicts(current.containerId, error)
    } catch (previewError) {
      settle()?.reject(toFriendlyError(previewError))
      return
    }
    if (raced && raced.length > 0) {
      showRacedConflicts(raced)
      return
    }
    if (isDecisionWithoutConflictError(error)) {
      try {
        await retryAfterVanishedConflict(current)
      } catch (retryError) {
        settle()?.reject(toFriendlyError(retryError))
      }
      return
    }
    const inline = legOverwriteInlineError(error)
    if (inline) {
      errorMessage.value = inline
      return
    }
    settle()?.reject(toFriendlyError(error))
  }

  async function confirm(legOverwrites: DSAttendanceLegOverwritePayload[]): Promise<void> {
    const current = pending
    if (!current || isSubmitting.value) return
    isSubmitting.value = true
    errorMessage.value = null
    try {
      await Network.dsAttendance.approveContainer(current.containerId, {
        ...current.basePayload,
        legOverwrites
      })
      settle()?.resolve('APPROVED')
    } catch (error) {
      await handleConfirmFailure(current, error)
    } finally {
      isSubmitting.value = false
    }
  }

  /** Marca os plantões com pagamento prioritário (os que têm valor a recalcular). */
  function markPriorityValues(status: EffectiveValueStatus): void {
    const next: Record<number, EffectiveValueStatus> = {}
    for (const conflict of conflicts.value) {
      if (conflict.priorityPayment) next[conflict.doctorStationId] = status
    }
    effectiveValueStatus.value = next
  }

  /**
   * D-W5 — chamado pelo `decisionsChange` do modal. Debounce + número de sequência: só a
   * resposta da ÚLTIMA decisão entra. Enquanto recalcula o valor fica PENDING (o número
   * anterior não é mais o da combinação atual); falha vira ERROR. O confirmar nunca trava.
   */
  function refreshEffectiveValues(legOverwrites: DSAttendanceLegOverwriteDecision[]): void {
    const current = pending
    if (!current) return
    clearRefresh()
    markPriorityValues('PENDING')
    const sequence = refreshSequence
    refreshTimer = setTimeout(async () => {
      refreshTimer = null
      try {
        const preview = await Network.dsAttendance.getLegOverwritePreview(
          current.containerId,
          legOverwrites
        )
        if (sequence !== refreshSequence || pending !== current) return
        const next: Record<number, EffectiveValue | null> = { ...effectiveValues.value }
        for (const conflict of preview?.conflicts ?? []) {
          next[conflict.doctorStationId] = conflict.effectiveValue
        }
        effectiveValues.value = next
        effectiveValueStatus.value = {}
      } catch (error) {
        if (sequence !== refreshSequence || pending !== current) return
        console.debug('[LegOverwrite] recálculo do valor indisponível', error)
        markPriorityValues('ERROR')
      }
    }, EFFECTIVE_VALUE_DEBOUNCE_MS)
  }

  function cancel(): void {
    if (isSubmitting.value) return
    settle()?.resolve('CANCELLED')
  }

  // Tela desmontada com o modal aberto: nenhum recálculo dispara depois e quem aguarda
  // o `approve()` não fica pendurado.
  if (getCurrentScope()) {
    onScopeDispose(() => {
      clearRefresh()
      settle()?.resolve('CANCELLED')
    })
  }

  return {
    isOpen,
    conflicts,
    effectiveValues,
    effectiveValueStatus,
    isSubmitting,
    errorMessage,
    staleNotice,
    approve,
    confirm,
    refreshEffectiveValues,
    cancel
  }
}
